/**
 * Multi-row review dialog for Reducto-extracted leased asset data.
 * Allows editing individual asset rows before batch confirmation.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { useConfirmLeasedAssetBatch } from '../../api/use-leased-asset-ocr';
import type { LeasedAssetRowConfirmation } from '../../types/ocr';

interface ExtractedAssetRow {
  asset_name?: string;
  asset_type?: string;
  energy_consumption?: number;
  energy_unit?: string;
  energy_source?: string;
  floor_area?: number;
  area_unit?: string;
  spend_amount?: number;
  currency?: string;
  country?: string;
  location?: string;
}

interface LeasedAssetReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  uploadId: string;
  extractedAssets: ExtractedAssetRow[];
  scope3Category: 8 | 13;
  reportingPeriodId: string;
  facilityId?: string | undefined;
  onSuccess?: () => void;
}

const ASSET_TYPE_OPTIONS = [
  { value: 'building', label: 'Building' },
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'data_center', label: 'Data Center' },
  { value: 'other', label: 'Other' },
];

export function LeasedAssetReviewDialog({
  open,
  onOpenChange,
  uploadId,
  extractedAssets,
  scope3Category,
  reportingPeriodId,
  facilityId,
  onSuccess,
}: LeasedAssetReviewDialogProps) {
  const { t } = useTranslation('ghg');
  const confirmMutation = useConfirmLeasedAssetBatch(uploadId);

  const [rows, setRows] = useState<LeasedAssetRowConfirmation[]>(() =>
    extractedAssets.map((asset, index) => ({
      rowIndex: index,
      assetName: asset.asset_name ?? `Asset ${String(index + 1)}`,
      assetType: asset.asset_type ?? 'building',
      energyConsumption: asset.energy_consumption,
      energyUnit: asset.energy_unit,
      energySource: asset.energy_source,
      floorArea: asset.floor_area,
      areaUnit: asset.area_unit,
      spendAmount: asset.spend_amount,
      currency: asset.currency,
      country: asset.country,
      location: asset.location,
    }))
  );

  const updateRow = (index: number, field: keyof LeasedAssetRowConfirmation, value: unknown) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const removeRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleConfirm = async () => {
    if (rows.length === 0) {
      toast.warning('No asset rows to confirm');
      return;
    }

    await confirmMutation.mutateAsync({
      scope3Category,
      reportingPeriodId,
      facilityId,
      assets: rows,
      storeAsEvidence: true,
    });

    onOpenChange(false);
    onSuccess?.();
  };

  const categoryLabel =
    scope3Category === 8 ? 'Upstream Leased Assets (Cat 8)' : 'Downstream Leased Assets (Cat 13)';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="leased-asset-review-title">
            Review Extracted Assets - {categoryLabel}
          </DialogTitle>
          <DialogDescription>
            Review and edit the extracted asset data before creating activity entries. Remove rows
            that should not be imported.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No asset rows to review. All rows have been removed.
            </p>
          ) : (
            <div className="space-y-3">
              {rows.map((row, index) => (
                <div
                  key={row.rowIndex}
                  className="rounded-lg border p-4 space-y-3"
                  data-testid={`leased-asset-row-${String(row.rowIndex)}`}
                >
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium">
                      Row {String(row.rowIndex + 1)}: {row.assetName}
                    </h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeRow(index)}
                      data-testid={`remove-row-${String(row.rowIndex)}`}
                    >
                      Remove
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground">Asset Name</label>
                      <Input
                        value={row.assetName}
                        onChange={(e) => updateRow(index, 'assetName', e.target.value)}
                        data-testid={`asset-name-${String(row.rowIndex)}`}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Asset Type</label>
                      <Select
                        value={row.assetType}
                        onValueChange={(value) => updateRow(index, 'assetType', value)}
                      >
                        <SelectTrigger data-testid={`asset-type-${String(row.rowIndex)}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ASSET_TYPE_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Energy</label>
                      <div className="flex gap-1">
                        <Input
                          type="number"
                          value={row.energyConsumption ?? ''}
                          onChange={(e) =>
                            updateRow(
                              index,
                              'energyConsumption',
                              e.target.value ? Number(e.target.value) : undefined
                            )
                          }
                          placeholder="kWh"
                          data-testid={`energy-consumption-${String(row.rowIndex)}`}
                        />
                        <Input
                          value={row.energyUnit ?? ''}
                          onChange={(e) =>
                            updateRow(index, 'energyUnit', e.target.value || undefined)
                          }
                          placeholder={t('leasedAssetReviewDialog.unit')}
                          className="w-16"
                          data-testid={`energy-unit-${String(row.rowIndex)}`}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Floor Area</label>
                      <div className="flex gap-1">
                        <Input
                          type="number"
                          value={row.floorArea ?? ''}
                          onChange={(e) =>
                            updateRow(
                              index,
                              'floorArea',
                              e.target.value ? Number(e.target.value) : undefined
                            )
                          }
                          placeholder={t('leasedAssetReviewDialog.area')}
                          data-testid={`floor-area-${String(row.rowIndex)}`}
                        />
                        <Input
                          value={row.areaUnit ?? ''}
                          onChange={(e) =>
                            updateRow(index, 'areaUnit', e.target.value || undefined)
                          }
                          placeholder="m2"
                          className="w-16"
                          data-testid={`area-unit-${String(row.rowIndex)}`}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Spend</label>
                      <div className="flex gap-1">
                        <Input
                          type="number"
                          value={row.spendAmount ?? ''}
                          onChange={(e) =>
                            updateRow(
                              index,
                              'spendAmount',
                              e.target.value ? Number(e.target.value) : undefined
                            )
                          }
                          placeholder={t('leasedAssetReviewDialog.amount')}
                          data-testid={`spend-amount-${String(row.rowIndex)}`}
                        />
                        <Input
                          value={row.currency ?? ''}
                          onChange={(e) =>
                            updateRow(index, 'currency', e.target.value || undefined)
                          }
                          placeholder="USD"
                          className="w-16"
                          data-testid={`currency-${String(row.rowIndex)}`}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Country</label>
                      <Input
                        value={row.country ?? ''}
                        onChange={(e) => updateRow(index, 'country', e.target.value || undefined)}
                        placeholder="US"
                        data-testid={`country-${String(row.rowIndex)}`}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Energy Source</label>
                      <Input
                        value={row.energySource ?? ''}
                        onChange={(e) =>
                          updateRow(index, 'energySource', e.target.value || undefined)
                        }
                        placeholder={t('leasedAssetReviewDialog.electricity')}
                        data-testid={`energy-source-${String(row.rowIndex)}`}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Location</label>
                      <Input
                        value={row.location ?? ''}
                        onChange={(e) =>
                          updateRow(index, 'location', e.target.value || undefined)
                        }
                        placeholder={t('leasedAssetReviewDialog.cityState')}
                        data-testid={`location-${String(row.rowIndex)}`}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-review">
            Cancel
          </Button>
          <Button
            onClick={() => void handleConfirm()}
            disabled={rows.length === 0 || confirmMutation.isPending}
            data-testid="confirm-batch-btn"
          >
            {confirmMutation.isPending
              ? 'Creating entries...'
              : `Confirm ${String(rows.length)} Asset${rows.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
